import type { User, Campaign, ScheduledComment, CommentMetrics, AuditLog, Notification, ApiUsageEstimate } from './types'

// Use environment-aware API endpoint
const API_BASE = import.meta.env.VITE_API_URL || 'https://reddit-autopilot.yugen37.workers.dev/api'

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('token')
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options?.headers,
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const error = await response.text()
    throw new ApiError(response.status, error || response.statusText)
  }

  return response.json()
}

// Auth API
export const authApi = {
  signup: (email: string, password: string) =>
    fetchApi<{ user: User; token: string }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  login: (email: string, password: string) =>
    fetchApi<{ user: User; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () =>
    fetchApi<void>('/auth/logout', {
      method: 'POST',
    }),

  me: () => fetchApi<{ user: User }>('/auth/me'),
}

// Campaign API
export const campaignApi = {
  list: () => fetchApi<{ campaigns: Campaign[] }>('/campaigns'),

  create: (campaign: Omit<Campaign, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) =>
    fetchApi<{ campaign: Campaign }>('/campaigns', {
      method: 'POST',
      body: JSON.stringify(campaign),
    }),

  update: (id: string, updates: Partial<Campaign>) =>
    fetchApi<{ campaign: Campaign }>(`/campaigns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),

  delete: (id: string) =>
    fetchApi<void>(`/campaigns/${id}`, {
      method: 'DELETE',
    }),

  activate: (id: string) =>
    fetchApi<{ campaign: Campaign }>(`/campaigns/${id}/activate`, {
      method: 'POST',
    }),

  deactivate: (id: string) =>
    fetchApi<{ campaign: Campaign }>(`/campaigns/${id}/deactivate`, {
      method: 'POST',
    }),

  estimateUsage: (config: Partial<Campaign>) =>
    fetchApi<ApiUsageEstimate>('/campaigns/estimate', {
      method: 'POST',
      body: JSON.stringify(config),
    }),
}

// Comment API
export const commentApi = {
  listPending: () => fetchApi<{ comments: ScheduledComment[] }>('/comments/pending'),

  approve: (id: string) =>
    fetchApi<{ comment: ScheduledComment }>(`/comments/${id}/approve`, {
      method: 'POST',
    }),

  reject: (id: string) =>
    fetchApi<{ comment: ScheduledComment }>(`/comments/${id}/reject`, {
      method: 'POST',
    }),

  edit: (id: string, newComment: string) =>
    fetchApi<{ comment: ScheduledComment }>(`/comments/${id}/edit`, {
      method: 'POST',
      body: JSON.stringify({ comment: newComment }),
    }),
}

// Analytics API
export const analyticsApi = {
  getComments: () =>
    fetchApi<{ comments: Array<ScheduledComment & { metrics?: CommentMetrics }> }>('/analytics/comments'),

  getCampaignStats: (campaignId: string) =>
    fetchApi<{
      totalComments: number
      averageUpvoteRatio: number
      shadowbanRate: number
    }>(`/analytics/campaigns/${campaignId}`),
}

// Activity API
export const activityApi = {
  list: (filters?: { campaignId?: string; actionType?: string; startDate?: string; endDate?: string }) => {
    const params = new URLSearchParams()
    if (filters?.campaignId) params.append('campaignId', filters.campaignId)
    if (filters?.actionType) params.append('actionType', filters.actionType)
    if (filters?.startDate) params.append('startDate', filters.startDate)
    if (filters?.endDate) params.append('endDate', filters.endDate)
    
    const query = params.toString()
    return fetchApi<{ logs: AuditLog[] }>(`/activity${query ? `?${query}` : ''}`)
  },
}

// Notification API
export const notificationApi = {
  list: () => fetchApi<{ notifications: Notification[] }>('/notifications'),

  dismiss: (id: string) =>
    fetchApi<void>(`/notifications/${id}/dismiss`, {
      method: 'POST',
    }),
}

// Reddit OAuth API
export const redditApi = {
  connect: () => fetchApi<{ authUrl: string }>('/reddit/connect'),

  disconnect: () =>
    fetchApi<void>('/reddit/disconnect', {
      method: 'POST',
    }),
}

// OpenAI API Key API
export const openaiApi = {
  setKey: (apiKey: string) =>
    fetchApi<void>('/openai/key', {
      method: 'POST',
      body: JSON.stringify({ apiKey }),
    }),

  validateKey: () => fetchApi<{ valid: boolean }>('/openai/validate'),
}
